// Tooltip — lightweight hover/focus tooltip with four placements. Shows on both
// mouseenter and focus for keyboard accessibility.
//
// ONE look, everywhere. There used to be a second `card` variant — a 230px light panel
// copying the charts' `.chart-tip` — for tips whose body is a heading plus a sentence.
// It meant the dashboard answered the same gesture with two different objects a few
// pixels apart (a dark pill on the vested chip, a white card on the source chip beside
// it). The pill now WRAPS instead of running off in one nowrap line, which is all the
// card was really providing, so every tooltip in the app is the same thing. Rich,
// multi-row hover cards INSIDE a chart plot are a different component (ChartTooltip).
//
// PORTALLED to <body> and positioned `fixed`, because an absolutely-positioned popover
// is trapped by its trigger's ancestors: the balance hero clips its overflow (for the
// corner glow), so a tip on the YTD-bucket row — the card's last row — had nowhere to
// go. It had no vertical fallback either, so a `side="top"` tip on a trigger near the
// top of the screen rendered ENTIRELY above the viewport, 115px up and invisible. Free
// of the ancestors, placement is decided in two parts: ACROSS the trigger, `side` is a
// preference, flipped to the opposite side when that side has no room in the viewport;
// ALONG its own edge, it slides to stay inside its WIDGET — the `.surface` card the
// trigger lives on — because a popover hanging off the card's edge onto the page
// background reads as broken even when it is technically on screen (reported on the
// first Balance-by-source pill, whose tip was clamped to the window and so overhung the
// hero card's left edge). Coordinates are written in a LAYOUT effect, before the browser
// paints, so the popover is never seen at its pre-measurement position.
// From the prototype.
import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import './tooltip.css';

// Run the tooltip's own show/hide AND whatever the caller passed for the same
// event. Spreading `rest` last would otherwise let a caller's handler REPLACE the
// toggle — JSX keeps the last of two same-named props — so a trigger that also
// wants the pointer event (the holdings allocation bar drives its cross-highlight
// from onMouseEnter/onFocus) silently killed the popover it asked for.
const compose = (own, theirs) => (event) => {
  own(event);
  theirs?.(event);
};

// Distance from the trigger, in px.
const GAP = 6;
// Breathing room kept between the popover and the edge of the viewport.
const EDGE = 8;

const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const NATIVE_FOCUSABLE = 'a[href], button, input, select, textarea';
const FOCUSABLE = `${NATIVE_FOCUSABLE}, [tabindex]:not([tabindex="-1"])`;

/** Does the popover fit on `at` side of the trigger, inside `bounds`? */
function fits(at, trg, tip, bounds) {
  if (at === 'top') return trg.top - GAP - tip.height >= bounds.top;
  if (at === 'bottom') return trg.bottom + GAP + tip.height <= bounds.bottom;
  if (at === 'left') return trg.left - GAP - tip.width >= bounds.left;
  return trg.right + GAP + tip.width <= bounds.right;
}

/**
 * The viewport, as a box the popover must stay inside. The LAYOUT box, not
 * `innerWidth`/`innerHeight`: those include the scrollbar gutter, so clamping to them
 * slides a right-edge tip up to ~15px under the scrollbar on Windows.
 */
function viewportBounds() {
  const view = document.documentElement;
  return {
    left: EDGE,
    top: EDGE,
    right: (view?.clientWidth || window.innerWidth) - EDGE,
    bottom: (view?.clientHeight || window.innerHeight) - EDGE,
  };
}

/**
 * The box the popover slides WITHIN: its widget (the `.surface` card the trigger sits on)
 * intersected with the viewport, so it can neither leave the screen nor hang off the card
 * onto the page background.
 *
 * A widget with less room than the popover needs is the one case this can't honour — a
 * 280px tip does not fit inside a 200px card — so there it gives up the widget and keeps
 * the viewport, which at least keeps the whole tip readable.
 */
function slideBounds(triggerEl, tip, view) {
  const widget = triggerEl?.closest?.('.surface');
  if (!widget) return view;
  const r = widget.getBoundingClientRect();
  const box = {
    left: Math.max(view.left, r.left + EDGE),
    top: Math.max(view.top, r.top + EDGE),
    right: Math.min(view.right, r.right - EDGE),
    bottom: Math.min(view.bottom, r.bottom - EDGE),
  };
  const roomAcross = box.right - box.left >= tip.width;
  const roomDown = box.bottom - box.top >= tip.height;
  return {
    left: roomAcross ? box.left : view.left,
    right: roomAcross ? box.right : view.right,
    top: roomDown ? box.top : view.top,
    bottom: roomDown ? box.bottom : view.bottom,
  };
}

/**
 * Viewport coordinates for the popover: on the side that has room, centred on the
 * trigger across that side, and slid along its own edge until it is inside its widget.
 * A tip that fits on neither side keeps the caller's `side` — better where the reader
 * expects it than flipped to another cramped spot.
 *
 * Note which box governs which axis. The FLIP is judged against the viewport, not the
 * widget: a popover may overhang its card across the trigger (it is portalled, so nothing
 * clips it), and demanding it fit inside the card would put the tips on a card's last row
 * back to having nowhere to go. The SLIDE is judged against the widget — see slideBounds.
 */
function placement(side, trg, tip, triggerEl) {
  const view = viewportBounds();
  const flipped = OPPOSITE[side];
  const at = fits(side, trg, tip, view) || !fits(flipped, trg, tip, view) ? side : flipped;
  const slide = slideBounds(triggerEl, tip, view);
  const midX = trg.left + trg.width / 2 - tip.width / 2;
  const midY = trg.top + trg.height / 2 - tip.height / 2;

  if (at === 'top' || at === 'bottom') {
    return {
      at,
      left: clamp(midX, slide.left, Math.max(slide.left, slide.right - tip.width)),
      top: at === 'top' ? trg.top - GAP - tip.height : trg.bottom + GAP,
    };
  }
  return {
    at,
    left: at === 'left' ? trg.left - GAP - tip.width : trg.right + GAP,
    top: clamp(midY, slide.top, Math.max(slide.top, slide.bottom - tip.height)),
  };
}

export function Tooltip({ children, content, side = 'top', as: Tag = 'span', className, ...rest }) {
  const [show, setShow] = useState(false);
  // Resolved viewport coordinates + the side the popover actually went to. Null until
  // measured (and in jsdom, where there is no layout to measure).
  const [pos, setPos] = useState(null);
  const tipRef = useRef(null);
  const wrapRef = useRef(null);
  const tipId = useId();
  // A plain span/div trigger is otherwise unreachable by keyboard, so its tip is mouse-only.
  // Left alone when the trigger is, or wraps, a focusable element: one tab stop, not two.
  const [needsTabStop, setNeedsTabStop] = useState(false);
  // When the trigger wraps a control, that control takes focus, so the description goes on it.
  const describedRef = useRef(null);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    describedRef.current = el && !el.matches(NATIVE_FOCUSABLE) ? el.querySelector(FOCUSABLE) : null;
    if (!el || rest.tabIndex !== undefined) return;
    // The trigger's own tabindex is ours to set, so only its tag, its children and its
    // ancestors count — a trigger inside a button (a sort header, a pickable row) stays plain.
    setNeedsTabStop(!el.matches(NATIVE_FOCUSABLE) && !el.querySelector(FOCUSABLE)
      && !el.parentElement?.closest(FOCUSABLE));
  }, [rest.tabIndex, children]);
  useEffect(() => {
    const inner = describedRef.current;
    if (!inner || !show) return undefined;
    const prev = inner.getAttribute('aria-describedby');
    inner.setAttribute('aria-describedby', prev ? `${prev} ${tipId}` : tipId);
    return () => (prev ? inner.setAttribute('aria-describedby', prev) : inner.removeAttribute('aria-describedby'));
  }, [show, tipId]);

  useLayoutEffect(() => {
    if (!show || !tipRef.current || !wrapRef.current) {
      setPos(null);
      return;
    }
    const tip = tipRef.current.getBoundingClientRect();
    const trg = wrapRef.current.getBoundingClientRect();
    // No layout to measure (jsdom, or a display:none ancestor): leave the popover where
    // the stylesheet puts it rather than placing it against a box of zeroes.
    if (!tip.width && !tip.height) return;
    setPos(placement(side, trg, tip, wrapRef.current));
  }, [show, side, content]);

  // A tip must not outlive the pointer resting on its trigger, and mouseleave alone
  // doesn't guarantee that: SCROLLING slides the trigger out from under a stationary
  // pointer without firing a single mouse event (the browser only re-evaluates hover on
  // the next pointer move), so the popover stayed up — and, being fixed-position content
  // at --z-dropdown, it rode up over the sticky top nav. Any ancestor scroll closes it
  // (capture, because scroll on an inner container doesn't bubble), as does a resize
  // (which moves the trigger out from under a placement already computed) and Escape,
  // which WCAG 1.4.13 requires of hover content.
  useEffect(() => {
    if (!show) return undefined;
    const hide = () => setShow(false);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('scroll', hide, { capture: true, passive: true });
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', hide, { capture: true });
      window.removeEventListener('resize', hide);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [show]);

  if (!content) {
    return (
      <Tag className={cx('tooltip-wrap--bare', className) || undefined} {...rest}>
        {children}
      </Tag>
    );
  }

  const popover = (
    <span
      ref={tipRef}
      id={tipId}
      className={cx('tooltip', `tooltip--${pos?.at || side}`)}
      role="tooltip"
      style={pos ? { left: `${pos.left}px`, top: `${pos.top}px` } : undefined}
    >
      {content}
    </span>
  );

  return (
    <Tag
      ref={wrapRef}
      className={cx('tooltip-wrap', className)}
      tabIndex={needsTabStop ? 0 : undefined}
      aria-describedby={show && !describedRef.current ? tipId : undefined}
      {...rest}
      onMouseEnter={compose(() => setShow(true), rest.onMouseEnter)}
      onMouseLeave={compose(() => setShow(false), rest.onMouseLeave)}
      onFocus={compose(() => setShow(true), rest.onFocus)}
      onBlur={compose(() => setShow(false), rest.onBlur)}
    >
      {children}
      {/* Portalled so no ancestor's `overflow` can clip it. `document.body` only exists
          in the browser/jsdom; a server render (none today) would fall back to inline. */}
      {show && (typeof document === 'undefined' ? popover : createPortal(popover, document.body))}
    </Tag>
  );
}

Tooltip.propTypes = {
  children: PropTypes.node,
  /** Tooltip body. If falsy, the child renders without tooltip behavior. */
  content: PropTypes.node,
  /** Preferred side. Flipped to the opposite one when that side has no room. */
  side: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  as: PropTypes.elementType,
  className: PropTypes.string,
};

export default Tooltip;
